import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import {
  CashDenomination,
  CashDenominationService,
  PunchStatus,
  SaleData,
} from '../../services/cash-denomination.service';
import { PageHeaderComponent } from '../shared/components/page-header/page-header.component';

export type CashDenominationMode = 'page' | 'dialog';
export type CashDenominationTab = 'dayIn' | 'dayOut';
export interface CashDenominationResult {
  action: 'saved' | 'skipped';
  tab: CashDenominationTab;
  total: number;
}

interface FixedDenomination {
  amount: number;
  dayInQuantity: number;
  dayInTotal: number;
  dayOutQuantity: number;
  dayOutTotal: number;
  dayCloseVerify: boolean;
}

@Component({
  selector: 'app-cash-denomination',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DropdownModule,
    ToastModule,
    ConfirmDialogModule,
    TabViewModule,
    CardModule,
    CheckboxModule,
    TooltipModule,
    PageHeaderComponent,
  ],
  providers: [CashDenominationService, MessageService, ConfirmationService],
  templateUrl: './cash-denomination.component.html',
  styleUrl: './cash-denomination.component.scss',
})
export class CashDenominationComponent implements OnInit {
  // Mode inputs for reusability
  @Input() mode: CashDenominationMode = 'page';
  @Input() initialTab: CashDenominationTab = 'dayIn';
  @Input() canSkip: boolean = true;
  @Output() onComplete = new EventEmitter<CashDenominationResult>();

  /** Dialog mode drops the page chrome (padding, app background) the global app-cash-denomination rule adds */
  @HostBinding('class.is-dialog') get isDialogMode(): boolean {
    return this.mode === 'dialog';
  }

  cashDenominationData: CashDenomination[] = [];
  dayInData: CashDenomination[] = [];
  dayCloseData: CashDenomination[] = [];
  cashDenominationForm: FormGroup;
  dayInTotal: number = 0;
  dayCloseTotal: number = 0;
  daySaleData: SaleData = {
    cardamount: '0',
    upiamount: '0',
    cashamount: '0',
    redeemamount: '0',
  };

  currentDate: Date = new Date();
  activeTabIndex: number = 0;

  fixedDenominations: FixedDenomination[] = [
    {
      amount: 1,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 2,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 5,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 10,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 20,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 50,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 100,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 200,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 500,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
    {
      amount: 2000,
      dayInQuantity: 0,
      dayInTotal: 0,
      dayOutQuantity: 0,
      dayOutTotal: 0,
      dayCloseVerify: false,
    },
  ];

  // Track unsaved changes
  hasDayInChanges: boolean = false;
  hasDayOutChanges: boolean = false;

  // Punch status - controls whether inputs are disabled
  isDayInDisabled: boolean = false;
  isDayOutDisabled: boolean = false;

  // Store original values for comparison
  originalDayInQuantities: { [key: number]: number } = {};
  originalDayOutQuantities: { [key: number]: number } = {};

  get allVerified(): boolean {
    return this.fixedDenominations.every((d) => d.dayCloseVerify);
  }

  set allVerified(value: boolean) {
    this.fixedDenominations.forEach((d) => (d.dayCloseVerify = value));
  }

  toggleVerifyAll(): void {
    const newValue = !this.allVerified;
    this.fixedDenominations.forEach((d) => (d.dayCloseVerify = newValue));
  }

  entryTypeOptions = [
    { label: 'Day In', value: 'dayIn' },
    { label: 'Day Out', value: 'dayOut' },
  ];

  amountOptions = [
    { label: 'Rs. 1', value: 1 },
    { label: 'Rs. 2', value: 2 },
    { label: 'Rs. 5', value: 5 },
    { label: 'Rs. 10', value: 10 },
    { label: 'Rs. 20', value: 20 },
    { label: 'Rs. 50', value: 50 },
    { label: 'Rs. 100', value: 100 },
    { label: 'Rs. 200', value: 200 },
    { label: 'Rs. 500', value: 500 },
    { label: 'Rs. 2000', value: 2000 },
  ];

  constructor(
    private cashDenominationService: CashDenominationService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    @Optional() public dialogRef: DynamicDialogRef,
    @Optional() public dialogConfig: DynamicDialogConfig,
  ) {
    this.cashDenominationForm = this.fb.group({
      entryType: ['dayIn', Validators.required],
      amount: [null, [Validators.required]],
      quantity: [null, [Validators.required, Validators.min(0)]],
    });

    // Calculate total when amount or quantity changes
    this.cashDenominationForm
      .get('amount')
      ?.valueChanges.subscribe(() => this.calculateTotal());
    this.cashDenominationForm
      .get('quantity')
      ?.valueChanges.subscribe(() => this.calculateTotal());
  }

  ngOnInit() {
    // Check if opened as dialog and apply config
    if (this.dialogConfig?.data) {
      this.mode = this.dialogConfig.data.mode || 'page';
      this.initialTab = this.dialogConfig.data.initialTab || 'dayIn';
      this.canSkip = this.dialogConfig.data.canSkip ?? true;
    }

    // Set active tab index based on initialTab (works for both @Input and dialogConfig)
    this.activeTabIndex = this.initialTab === 'dayIn' ? 0 : 1;

    this.loadPunchStatus();
    this.loadCashDenominationData();
    this.loadSaleValueData();
  }

  private loadPunchStatus(): void {
    // First try to get from localStorage
    const storedStatus = localStorage.getItem('punchStatus');
    if (storedStatus) {
      try {
        const punchStatus: PunchStatus = JSON.parse(storedStatus);
        this.isDayInDisabled = punchStatus.hasCashInPunched;
        this.isDayOutDisabled = punchStatus.hasCashOutPunched;
        return;
      } catch {
        // Fall through to API call
      }
    }

    // Fetch from API if not in localStorage
    this.cashDenominationService.getPunchStatus().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.isDayInDisabled = response.data.hasCashInPunched;
          this.isDayOutDisabled = response.data.hasCashOutPunched;
          localStorage.setItem('punchStatus', JSON.stringify(response.data));
        }
      },
      error: () => {
        // Keep defaults (not disabled)
      },
    });
  }

  loadSaleValueData() {
    // Load Day In data
    this.cashDenominationService.getDayPaymentwiseCount().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.daySaleData = response.data;
        } else {
          this.daySaleData = {
            cardamount: '0',
            upiamount: '0',
            cashamount: '0',
            redeemamount: '0',
          };
        }
      },
      error: () => {
        this.daySaleData = {
          cardamount: '0',
          upiamount: '0',
          cashamount: '0',
          redeemamount: '0',
        };
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Day In data',
        });
      },
    });
  }

  loadCashDenominationData() {
    let dayInLoaded = false;
    let dayOutLoaded = false;

    // Load Day In data
    this.cashDenominationService.getDayInCashDenominations().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.dayInData = response.data;
        } else {
          this.dayInData = [];
        }
        dayInLoaded = true;
        if (dayInLoaded && dayOutLoaded) {
          this.populateFixedDenominations();
        }
      },
      error: () => {
        this.dayInData = [];
        dayInLoaded = true;
        if (dayInLoaded && dayOutLoaded) {
          this.populateFixedDenominations();
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Day In data',
        });
      },
    });

    // Load Day Out data
    this.cashDenominationService.getDayOutCashDenominations().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.dayCloseData = response.data;
        } else {
          this.dayCloseData = [];
        }
        dayOutLoaded = true;
        if (dayInLoaded && dayOutLoaded) {
          this.populateFixedDenominations();
        }
      },
      error: () => {
        this.dayCloseData = [];
        dayOutLoaded = true;
        if (dayInLoaded && dayOutLoaded) {
          this.populateFixedDenominations();
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Day Out data',
        });
      },
    });
  }

  populateFixedDenominations() {
    // Reset all quantities
    this.fixedDenominations.forEach((denom) => {
      denom.dayInQuantity = 0;
      denom.dayInTotal = 0;
      denom.dayOutQuantity = 0;
      denom.dayOutTotal = 0;
    });

    // Populate Day In quantities
    this.dayInData.forEach((item) => {
      const denom = this.fixedDenominations.find(
        (d) => d.amount === item.amount,
      );
      if (denom) {
        denom.dayInQuantity += item.quantity || 0;
        denom.dayInTotal += item.total || 0;
      }
    });

    // Populate Day Out quantities
    this.dayCloseData.forEach((item) => {
      const denom = this.fixedDenominations.find(
        (d) => d.amount === item.amount,
      );
      if (denom) {
        denom.dayOutQuantity += item.quantity || 0;
        denom.dayOutTotal += item.total || 0;
      }
    });

    // Store original values for change tracking
    this.storeOriginalValues();
    this.updateTotals();
  }

  storeOriginalValues() {
    this.originalDayInQuantities = {};
    this.originalDayOutQuantities = {};

    this.fixedDenominations.forEach((denom) => {
      this.originalDayInQuantities[denom.amount] = denom.dayInQuantity;
      this.originalDayOutQuantities[denom.amount] = denom.dayOutQuantity;
    });

    // Reset change flags
    this.hasDayInChanges = false;
    this.hasDayOutChanges = false;
  }

  loadTotals() {
    // Calculate totals from fixed denominations
    this.updateTotals();
  }

  calculateTotal(): number {
    const amount = this.cashDenominationForm.get('amount')?.value || 0;
    const quantity = this.cashDenominationForm.get('quantity')?.value || 0;
    return amount * quantity;
  }

  onSubmit() {
    if (this.cashDenominationForm.valid) {
      const formData = this.cashDenominationForm.value;
      const newEntry = {
        amount: formData.amount,
        quantity: formData.quantity,
        total: this.calculateTotal(),
        date: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD
        warehouse_id: 1,
        createdBy: 1,
        type: formData.entryType === 'dayIn' ? 0 : 1, // 0 = Day In, 1 = Day Out
        // Legacy fields for backward compatibility
        dayInVerify: 'Checked' as const,
        dayCloseVerify: (formData.dayCloseVerify
          ? 'Checked'
          : 'Not Checked') as 'Checked' | 'Not Checked',
        entryType: formData.entryType,
      };

      this.createCashDenomination(newEntry);
    }
  }

  createCashDenomination(entry: CashDenomination & { entryType: string }) {
    const isDayIn = entry.entryType === 'dayIn';
    const apiCall = isDayIn
      ? this.cashDenominationService.createDayInCashDenomination(entry)
      : this.cashDenominationService.createDayOutCashDenomination(entry);

    apiCall.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${isDayIn ? 'Day In' : 'Day Out'} cash denomination entry added successfully`,
        });
        this.resetForm();
        this.loadCashDenominationData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to add ${isDayIn ? 'Day In' : 'Day Out'} cash denomination entry`,
        });
      },
    });
  }

  resetForm() {
    const resetValues: any = {
      entryType: this.activeTabIndex === 0 ? 'dayIn' : 'dayOut',
    };

    if (this.cashDenominationForm.get('dayCloseVerify')) {
      resetValues.dayCloseVerify = false;
    }

    this.cashDenominationForm.reset(resetValues);
  }

  calculateRowTotal(entry: CashDenomination) {
    entry.total = (entry.amount || 0) * (entry.quantity || 0);
    this.loadTotals();
  }

  isRowValid(entry: CashDenomination): boolean {
    return !!(entry.amount && entry.quantity && entry.quantity > 0);
  }

  saveAllChanges() {
    if (this.hasDayInChanges) {
      this.saveAllDayIn();
    }
    if (this.hasDayOutChanges) {
      this.saveAllDayOut();
    }
    if (!this.hasDayInChanges && !this.hasDayOutChanges) {
      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'No changes to save',
      });
    }
  }

  // Fixed denomination methods
  calculateDenominationTotal(denom: FixedDenomination) {
    // Ensure quantities are not negative
    if (denom.dayInQuantity < 0) {
      denom.dayInQuantity = 0;
    }
    if (denom.dayOutQuantity < 0) {
      denom.dayOutQuantity = 0;
    }

    denom.dayInTotal = denom.amount * denom.dayInQuantity;
    denom.dayOutTotal = denom.amount * denom.dayOutQuantity;
    this.updateTotals();
    this.checkForChanges();
  }

  getTotalItemsCount(tab: 'dayIn' | 'dayOut'): number {
    return this.fixedDenominations.reduce((sum, denom) => {
      return sum + (tab === 'dayIn' ? (denom.dayInQuantity || 0) : (denom.dayOutQuantity || 0));
    }, 0);
  }

  focusNextDenomination(currentIndex: number, tab: string): void {
    const nextInput = document.getElementById(`denom_${tab}_${currentIndex + 1}`) as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  }

  checkForChanges() {
    // Check Day In changes
    this.hasDayInChanges = this.fixedDenominations.some(
      (denom) =>
        denom.dayInQuantity !==
        (this.originalDayInQuantities[denom.amount] || 0),
    );

    // Check Day Out changes
    this.hasDayOutChanges = this.fixedDenominations.some(
      (denom) =>
        denom.dayOutQuantity !==
        (this.originalDayOutQuantities[denom.amount] || 0),
    );
  }

  updateTotals() {
    this.dayInTotal = this.fixedDenominations.reduce(
      (sum, denom) => sum + denom.dayInTotal,
      0,
    );
    this.dayCloseTotal = this.fixedDenominations.reduce(
      (sum, denom) => sum + denom.dayOutTotal,
      0,
    );
  }

  // Section-wise save methods
  saveAllDayIn() {
    const dayInEntries = this.fixedDenominations.map((denom) => ({
      amount: denom.amount,
      quantity: denom.dayInQuantity,
    }));

    const totalAmount = this.dayInTotal;

    this.cashDenominationService
      .createBatchCashDenominationWithType(dayInEntries, 0)
      .subscribe({
        next: () => {
          const successMessage =
            totalAmount === 0
              ? 'Day In recorded with ₹0 opening cash'
              : `Day In: ${dayInEntries.length} entries saved successfully`;

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: successMessage,
          });

          this.hasDayInChanges = false;
          this.fixedDenominations.forEach((denom) => {
            this.originalDayInQuantities[denom.amount] = denom.dayInQuantity;
          });
          this.loadCashDenominationData();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save Day In entries',
          });
        },
      });
  }

  saveAllDayOut() {
    // Include ALL denominations, even with zero quantity (matching Day In behavior)
    const dayOutEntries = this.fixedDenominations.map((denom) => ({
      amount: denom.amount,
      quantity: denom.dayOutQuantity || 0, // Ensure 0 instead of undefined/null
      verified: denom.dayCloseVerify,
    }));

    const totalAmount = this.dayCloseTotal;

    this.cashDenominationService
      .createBatchCashDenominationWithType(dayOutEntries, 1)
      .subscribe({
        next: () => {
          const successMessage =
            totalAmount === 0
              ? 'Day Out recorded with ₹0 closing cash'
              : `Day Out: ${dayOutEntries.length} entries saved successfully`;

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: successMessage,
          });

          this.hasDayOutChanges = false;
          this.fixedDenominations.forEach((denom) => {
            this.originalDayOutQuantities[denom.amount] = denom.dayOutQuantity;
          });
          this.loadCashDenominationData();

          if (this.mode === 'page') {
            this.refreshPunchStatusFromApi();
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save Day Out entries',
          });
        },
      });
  }

  saveAllDayOutAndClose(result: CashDenominationResult) {
    const dayOutEntries = this.fixedDenominations.map((denom) => ({
      amount: denom.amount,
      quantity: denom.dayOutQuantity || 0, // Ensure 0 instead of undefined/null
      verified: denom.dayCloseVerify,
    }));

    const totalAmount = this.dayCloseTotal;

    this.cashDenominationService
      .createBatchCashDenominationWithType(dayOutEntries, 1)
      .subscribe({
        next: () => {
          const successMessage =
            totalAmount === 0
              ? 'Day Out recorded with ₹0 closing cash'
              : `Day Out: ${dayOutEntries.length} entries saved successfully`;

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: successMessage,
          });

          this.hasDayOutChanges = false;
          this.fixedDenominations.forEach((denom) => {
            this.originalDayOutQuantities[denom.amount] = denom.dayOutQuantity;
          });

          this.onComplete.emit(result);
          if (this.dialogRef) {
            this.dialogRef.close(result);
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save Day Out entries',
          });

          this.onComplete.emit(result);
          if (this.dialogRef) {
            this.dialogRef.close(result);
          }
        },
      });
  }

  saveAllDayInAndClose(result: CashDenominationResult) {
    const dayInEntries = this.fixedDenominations.map((denom) => ({
      amount: denom.amount,
      quantity: denom.dayInQuantity || 0, // Ensure 0 instead of undefined/null
    }));

    const totalAmount = this.dayInTotal;

    this.cashDenominationService
      .createBatchCashDenominationWithType(dayInEntries, 0)
      .subscribe({
        next: () => {
          const successMessage =
            totalAmount === 0
              ? 'Day In recorded with ₹0 opening cash'
              : `Day In: ${dayInEntries.length} entries saved successfully`;

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: successMessage,
          });

          this.hasDayInChanges = false;
          this.fixedDenominations.forEach((denom) => {
            this.originalDayInQuantities[denom.amount] = denom.dayInQuantity;
          });

          this.onComplete.emit(result);
          if (this.dialogRef) {
            this.dialogRef.close(result);
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save Day In entries',
          });

          // Still close dialog even on error
          this.onComplete.emit(result);
          if (this.dialogRef) {
            this.dialogRef.close(result);
          }
        },
      });
  }

  private refreshPunchStatusFromApi(): void {
    this.cashDenominationService.getPunchStatus().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.isDayInDisabled = response.data.hasCashInPunched;
          this.isDayOutDisabled = response.data.hasCashOutPunched;
          localStorage.setItem('punchStatus', JSON.stringify(response.data));
        }
      },
      error: () => {
        this.isDayOutDisabled = true;
      },
    });
  }

  onTabChange(event: any) {
    this.activeTabIndex = event.index;

    this.cashDenominationForm.patchValue({
      entryType: this.activeTabIndex === 0 ? 'dayIn' : 'dayOut',
    });

    if (this.activeTabIndex === 1) {
      // Day Close tab
      if (!this.cashDenominationForm.get('dayCloseVerify')) {
        this.cashDenominationForm.addControl(
          'dayCloseVerify',
          this.fb.control(false),
        );
      }
    } else {
      // Day In tab
      if (this.cashDenominationForm.get('dayCloseVerify')) {
        this.cashDenominationForm.removeControl('dayCloseVerify');
      }
    }
  }

  dayIn() {
    // Day In functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Day In',
      detail: 'Day In process initiated',
    });
  }

  dayClose() {
    // Day Close functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Day Close',
      detail: 'Day Close process initiated',
    });
  }

  toInt(number: string) {
    //console.log(number)
    if (number == null) return 0;
    else return parseInt(number);
  }

  goBackToHome() {
    this.router.navigate(['/dashboard']);
  }

  // Dialog mode methods
  saveAndContinue() {
    const isDayIn = this.activeTabIndex === 0;
    const total = isDayIn ? this.dayInTotal : this.dayCloseTotal;

    // Day In (Opening Cash): Allow ₹0 with confirmation (for first-time setup)
    if (isDayIn && total === 0) {
      this.confirmationService.confirm({
        message:
          'Opening cash amount is ₹0. This is only acceptable for the very first time when setting up a new counter. Are you sure you want to continue?',
        header: 'Zero Opening Cash Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Continue',
        rejectLabel: 'No, Go Back',
        accept: () => {
          this.proceedWithSave(isDayIn, total);
        },
        reject: () => {
          this.messageService.add({
            severity: 'info',
            summary: 'Cancelled',
            detail: 'Please enter the opening cash denominations',
          });
        },
      });
      return; // Don't proceed until user confirms
    }

    // Day Out (Closing Cash): Do NOT allow ₹0 when saving
    // User must either enter actual cash amounts OR use Skip button
    if (!isDayIn && total === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Amount',
        detail:
          'Closing cash amount cannot be ₹0. Please enter the actual cash denominations or use the Skip button.',
        life: 5000,
      });
      return;
    }

    this.proceedWithSave(isDayIn, total);
  }

  private proceedWithSave(isDayIn: boolean, total: number) {
    const result: CashDenominationResult = {
      action: 'saved',
      tab: isDayIn ? 'dayIn' : 'dayOut',
      total: total,
    };

    // Save the current tab data and wait for completion before closing dialog
    if (isDayIn) {
      this.saveAllDayInAndClose(result);
    } else {
      this.saveAllDayOutAndClose(result);
    }
  }

  skip() {
    if (!this.canSkip) return;

    const isDayIn = this.activeTabIndex === 0;
    const result: CashDenominationResult = {
      action: 'skipped',
      tab: isDayIn ? 'dayIn' : 'dayOut',
      total: 0,
    };

    // Emit result and close dialog
    this.onComplete.emit(result);
    if (this.dialogRef) {
      this.dialogRef.close(result);
    }
  }

  // Getter for dialog title
  get dialogTitle(): string {
    return this.initialTab === 'dayIn' ? 'Cash In' : 'Cash Out';
  }
}
